import { Experiment, Variant } from '@amplitude/experiment-node-server';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { CURRENT_SUBSCRIBER, CURRENT_ORGANIZATION } from '@lib/queries';
import { CurrentSubscriberQuery, CurrentOrganizationQuery } from '@generated/types';
import { initializeApollo } from '@lib/useApollo';
import { apiConfig } from '@lib/apiConfig';
import { IncomingMessage } from 'http';

const DEPLOYMENT_KEY = process.env.AMPLITUDE_SERVER_DEPLOYMENT_KEY || '';

/**
 * User object for Amplitude experiments
 */
export interface AmplitudeUser {
  user_id: string;
  organization_id?: string;
  user_properties?: {
    organization_id: string;
  };
}

/**
 * Experiments result from Amplitude
 * @example { 'bible-app-link': { key: 'on', value: 'on', ... } }
 */
export type ExperimentsResult = Record<string, Variant>;

/**
 * Fetches authenticated user's ID and organization ID from GraphQL
 * @param apolloClient - Apollo client with authentication
 * @returns User and organization IDs, or null if not found
 */
export async function fetchUserDataForAmplitude(
  apolloClient: ApolloClient<NormalizedCacheObject> | ApolloClient<unknown>
): Promise<{ userId: string; organizationId: string } | null> {
  try {
    const [subscriberResult, organizationResult] = await Promise.all([
      apolloClient.query<CurrentSubscriberQuery>({
        query: CURRENT_SUBSCRIBER,
      }),
      apolloClient.query<CurrentOrganizationQuery>({
        query: CURRENT_ORGANIZATION,
      }),
    ]);

    const userId = subscriberResult.data?.currentSubscriber?.id;
    const organizationId = organizationResult.data?.currentOrganization?.id;

    if (!userId || !organizationId) {
      console.warn('[Amplitude] User or organization not found');
      return null;
    }

    return { userId, organizationId };
  } catch (error) {
    console.error('[Amplitude] Error fetching user data:', error);
    return null;
  }
}

/**
 * Fetches experiments from Amplitude for a specific user
 * @param userId - User ID (subscriber ID)
 * @param organizationId - Organization ID
 * @returns Experiments with their assigned variants
 */
export async function fetchAmplitudeExperiments(
  userId: string,
  organizationId: string
): Promise<ExperimentsResult> {
  if (!DEPLOYMENT_KEY) {
    console.warn('[Amplitude] No deployment key found');
    return {};
  }

  try {
    const client = Experiment.initialize(DEPLOYMENT_KEY);

    const user: AmplitudeUser = {
      user_id: userId,
      organization_id: organizationId,
      user_properties: {
        organization_id: organizationId,
      },
    };

    const variants = await client.fetchV2(user);

    console.log('[Amplitude] Experiments fetched');

    return variants;
  } catch (error) {
    console.error('[Amplitude] Error fetching experiments:', error);
    return {};
  }
}

/**
 * All-in-one helper to fetch Amplitude experiments in getInitialProps
 *
 * This function handles:
 * - Authentication (forwards request cookies)
 * - Apollo client initialization with auth
 * - Fetching user data (userId + organizationId)
 * - Fetching experiments from Amplitude
 *
 * @param request - IncomingMessage from server (ctx.req in getInitialProps)
 * @returns Experiments object, or empty object if fails
 *
 * @example
 * ```ts
 * MyApp.getInitialProps = async (appContext: AppContext) => {
 *   const amplitudeExperiments = await fetchAmplitudeExperimentsForRequest(appContext?.ctx.req);
 *   return { amplitudeExperiments };
 * };
 * ```
 */
export async function fetchAmplitudeExperimentsForRequest(
  request?: IncomingMessage
): Promise<ExperimentsResult> {
  // Server-side only
  if (typeof window !== 'undefined' || !request) {
    return {};
  }

  try {
    const config = apiConfig(request.headers);

    // Create custom fetch that includes request cookies for authentication
    const serverFetch = (url: RequestInfo, options?: RequestInit): Promise<Response> => {
      const headers = {
        ...options?.headers,
        cookie: request.headers.cookie || '',
      };
      return fetch(url, { ...options, headers });
    };

    // Initialize Apollo with authenticated fetch
    const apolloClient = initializeApollo(config, null, serverFetch);

    // Get user data from GraphQL
    const userData = await fetchUserDataForAmplitude(apolloClient);

    if (!userData) {
      console.warn('[Amplitude] No user data available');
      return {};
    }

    const { organizationId, userId } = userData;
    console.log('[Amplitude] Fetching experiments');

    // Fetch experiments
    return await fetchAmplitudeExperiments(userId, organizationId);
  } catch (error) {
    console.error('[Amplitude] Error in fetchAmplitudeExperimentsForRequest:', error);
    return {};
  }
}
